/* ═══════════════════════════════════════════════════════════════════
   calculateRfwScore() — Motor de puntuación RFW
   BLOQUE 1 DE 10 — Núcleo de Competencia
   
   Reglas de puntuación según documentación del sistema RFW Score.
   No modificar manualmente. Solo a través de esta función central.
   ═══════════════════════════════════════════════════════════════════ */

import type { ScoreCategory } from '@/mocks/rfwScores';

/* ─── Point Values ─── */
const RADIO_POINTS: Record<string, number> = {
  hora_radio: 1,
  post_publicado: 5,
  evento_creado: 15,
  concurso_creado: 25,
  programa_activo: 10,
  locutor_activo: 10,
  comentario_recibido: 1,
  like_recibido: 1,
  compartido_recibido: 5,
  nuevo_seguidor: 10,
  nuevo_miembro_club: 15,
  saludo_recibido: 2,
  cancion_solicitada: 2,
  encuesta_respondida: 2,
  participacion_concurso: 5,
  ganador_concurso: 10,
  invitado_nuevo: 20,
  verificacion_inicial: 100,
};

const RADIO_PENALTIES: Record<string, number> = {
  inactividad_7_dias: -50,
  sin_publicaciones_14_dias: -100,
  sin_programas_activos: -50,
  sin_interaccion_real: -100,
  reporte_moderacion: -25,
  spam_confirmado: -100,
};

const PROGRAMA_POINTS: Record<string, number> = {
  programa_iniciado: 10,
  nuevo_seguidor: 5,
  comentario: 1,
  like: 1,
  compartido: 3,
  participacion_concurso: 3,
  mencion: 2,
  escucha_activa: 1,
};

const LOCUTOR_POINTS: Record<string, number> = {
  inicio_transmision: 10,
  nuevo_seguidor: 5,
  like: 1,
  compartido: 5,
  saludo_recibido: 2,
  solicitud_cancion: 2,
  comentario: 1,
  participacion_concurso: 3,
  evento_creado: 10,
};

const OYENTE_POINTS: Record<string, number> = {
  crear_perfil: 20,
  primer_saludo: 10,
  primer_comentario: 10,
  primera_cancion: 10,
  seguir_radio: 5,
  seguir_programa: 5,
  seguir_locutor: 5,
  compartir_contenido: 10,
  participar_concurso: 15,
  ganar_concurso: 50,
  asistir_evento: 20,
  invitar_amigo: 25,
};

const CLUB_POINTS: Record<string, number> = {
  nuevo_miembro: 10,
  actividad_miembro: 1,
  compartido_club: 5,
  evento_club: 10,
  concurso_club: 15,
  victoria_competencia: 50,
};

/* ─── Category-to-Action Mapping ─── */
type EntityType = 'radio' | 'programa' | 'locutor' | 'oyente' | 'club';

export interface ScoreAction {
  category: ScoreCategory;
  action: string;
  points: number;
  entityType: EntityType;
}

export const ALL_POINT_ACTIONS: ScoreAction[] = [
  // Radio
  { category: 'horas_radio', action: 'Radio encendida (por hora)', points: RADIO_POINTS.hora_radio, entityType: 'radio' },
  { category: 'publicaciones', action: 'Post publicado', points: RADIO_POINTS.post_publicado, entityType: 'radio' },
  { category: 'eventos', action: 'Evento creado', points: RADIO_POINTS.evento_creado, entityType: 'radio' },
  { category: 'concursos', action: 'Concurso creado', points: RADIO_POINTS.concurso_creado, entityType: 'radio' },
  { category: 'programas', action: 'Programa activo', points: RADIO_POINTS.programa_activo, entityType: 'radio' },
  { category: 'locutores', action: 'Locutor activo', points: RADIO_POINTS.locutor_activo, entityType: 'radio' },
  { category: 'comentarios', action: 'Comentario recibido', points: RADIO_POINTS.comentario_recibido, entityType: 'radio' },
  { category: 'likes', action: 'Like recibido', points: RADIO_POINTS.like_recibido, entityType: 'radio' },
  { category: 'compartidos', action: 'Compartido recibido', points: RADIO_POINTS.compartido_recibido, entityType: 'radio' },
  { category: 'seguidores', action: 'Nuevo seguidor', points: RADIO_POINTS.nuevo_seguidor, entityType: 'radio' },
  { category: 'clubes', action: 'Nuevo miembro Club', points: RADIO_POINTS.nuevo_miembro_club, entityType: 'radio' },
  { category: 'saludos', action: 'Saludo recibido', points: RADIO_POINTS.saludo_recibido, entityType: 'radio' },
  { category: 'canciones', action: 'Canción solicitada', points: RADIO_POINTS.cancion_solicitada, entityType: 'radio' },
  { category: 'encuestas', action: 'Encuesta respondida', points: RADIO_POINTS.encuesta_respondida, entityType: 'radio' },
  { category: 'participacion_concursos', action: 'Participación concurso', points: RADIO_POINTS.participacion_concurso, entityType: 'radio' },
  { category: 'ganador_concurso', action: 'Ganador concurso', points: RADIO_POINTS.ganador_concurso, entityType: 'radio' },
  { category: 'invitados', action: 'Invitado nuevo registrado', points: RADIO_POINTS.invitado_nuevo, entityType: 'radio' },
  { category: 'verificacion', action: 'Radio verificada (inicial)', points: RADIO_POINTS.verificacion_inicial, entityType: 'radio' },
  // Programa
  { category: 'programas', action: 'Programa iniciado', points: PROGRAMA_POINTS.programa_iniciado, entityType: 'programa' },
  { category: 'seguidores', action: 'Nuevo seguidor', points: PROGRAMA_POINTS.nuevo_seguidor, entityType: 'programa' },
  { category: 'comentarios', action: 'Comentario', points: PROGRAMA_POINTS.comentario, entityType: 'programa' },
  { category: 'likes', action: 'Like', points: PROGRAMA_POINTS.like, entityType: 'programa' },
  { category: 'compartidos', action: 'Compartido', points: PROGRAMA_POINTS.compartido, entityType: 'programa' },
  { category: 'participacion_concursos', action: 'Participación concurso', points: PROGRAMA_POINTS.participacion_concurso, entityType: 'programa' },
  { category: 'menciones', action: 'Mención', points: PROGRAMA_POINTS.mencion, entityType: 'programa' },
  { category: 'escuchas_activas', action: 'Escucha activa', points: PROGRAMA_POINTS.escucha_activa, entityType: 'programa' },
  // Locutor
  { category: 'transmisiones', action: 'Inicio transmisión', points: LOCUTOR_POINTS.inicio_transmision, entityType: 'locutor' },
  { category: 'seguidores', action: 'Nuevo seguidor', points: LOCUTOR_POINTS.nuevo_seguidor, entityType: 'locutor' },
  { category: 'likes', action: 'Like', points: LOCUTOR_POINTS.like, entityType: 'locutor' },
  { category: 'compartidos', action: 'Compartido', points: LOCUTOR_POINTS.compartido, entityType: 'locutor' },
  { category: 'saludos', action: 'Saludo recibido', points: LOCUTOR_POINTS.saludo_recibido, entityType: 'locutor' },
  { category: 'canciones', action: 'Solicitud canción', points: LOCUTOR_POINTS.solicitud_cancion, entityType: 'locutor' },
  { category: 'comentarios', action: 'Comentario', points: LOCUTOR_POINTS.comentario, entityType: 'locutor' },
  { category: 'participacion_concursos', action: 'Participación concurso', points: LOCUTOR_POINTS.participacion_concurso, entityType: 'locutor' },
  { category: 'eventos', action: 'Evento creado', points: LOCUTOR_POINTS.evento_creado, entityType: 'locutor' },
  // Oyente
  { category: 'perfil_creado', action: 'Crear perfil', points: OYENTE_POINTS.crear_perfil, entityType: 'oyente' },
  { category: 'saludos', action: 'Primer saludo', points: OYENTE_POINTS.primer_saludo, entityType: 'oyente' },
  { category: 'comentarios', action: 'Primer comentario', points: OYENTE_POINTS.primer_comentario, entityType: 'oyente' },
  { category: 'canciones', action: 'Primera canción', points: OYENTE_POINTS.primera_cancion, entityType: 'oyente' },
  { category: 'seguidores', action: 'Seguir radio', points: OYENTE_POINTS.seguir_radio, entityType: 'oyente' },
  { category: 'seguidores', action: 'Seguir programa', points: OYENTE_POINTS.seguir_programa, entityType: 'oyente' },
  { category: 'seguidores', action: 'Seguir locutor', points: OYENTE_POINTS.seguir_locutor, entityType: 'oyente' },
  { category: 'compartidos', action: 'Compartir contenido', points: OYENTE_POINTS.compartir_contenido, entityType: 'oyente' },
  { category: 'participacion_concursos', action: 'Participar concurso', points: OYENTE_POINTS.participar_concurso, entityType: 'oyente' },
  { category: 'ganador_concurso', action: 'Ganar concurso', points: OYENTE_POINTS.ganar_concurso, entityType: 'oyente' },
  { category: 'asistir_evento', action: 'Asistir evento', points: OYENTE_POINTS.asistir_evento, entityType: 'oyente' },
  { category: 'invitar_amigo', action: 'Invitar amigo', points: OYENTE_POINTS.invitar_amigo, entityType: 'oyente' },
  // Club
  { category: 'clubes', action: 'Nuevo miembro', points: CLUB_POINTS.nuevo_miembro, entityType: 'club' },
  { category: 'actividad_miembro', action: 'Actividad miembro', points: CLUB_POINTS.actividad_miembro, entityType: 'club' },
  { category: 'compartidos', action: 'Compartido club', points: CLUB_POINTS.compartido_club, entityType: 'club' },
  { category: 'eventos', action: 'Evento club', points: CLUB_POINTS.evento_club, entityType: 'club' },
  { category: 'concursos', action: 'Concurso club', points: CLUB_POINTS.concurso_club, entityType: 'club' },
  { category: 'victoria_competencia', action: 'Victoria competencia', points: CLUB_POINTS.victoria_competencia, entityType: 'club' },
];

/* ─── Penalty Actions ─── */
export const ALL_PENALTY_ACTIONS = [
  { action: 'Sin actividad 7 días', points: RADIO_PENALTIES.inactividad_7_dias, entityType: 'radio' as EntityType },
  { action: 'Sin publicaciones 14 días', points: RADIO_PENALTIES.sin_publicaciones_14_dias, entityType: 'radio' as EntityType },
  { action: 'Sin programas activos', points: RADIO_PENALTIES.sin_programas_activos, entityType: 'radio' as EntityType },
  { action: 'Sin interacción real', points: RADIO_PENALTIES.sin_interaccion_real, entityType: 'radio' as EntityType },
  { action: 'Reporte moderación confirmado', points: RADIO_PENALTIES.reporte_moderacion, entityType: 'radio' as EntityType },
  { action: 'Spam confirmado', points: RADIO_PENALTIES.spam_confirmado, entityType: 'radio' as EntityType },
];

/* ─── Core Calculate Function ─── */
export function calculateRfwScore(
  entityType: EntityType,
  actions: { category: ScoreCategory; count: number }[],
): number {
  const actionMap = ALL_POINT_ACTIONS.filter(a => a.entityType === entityType);
  let total = 0;

  for (const userAction of actions) {
    const match = actionMap.find(a => a.category === userAction.category);
    if (match) {
      total += match.points * userAction.count;
    }
  }

  return Math.max(0, total);
}

/* ─── Score Trend Helper ─── */
export function determineScoreTrend(
  scoreChange: number,
  currentScore: number,
): 'exploding' | 'trending' | 'growing' | 'active' | 'stable' | 'declining' {
  const pctChange = currentScore > 0 ? (scoreChange / currentScore) * 100 : 0;

  if (pctChange > 15) return 'exploding';
  if (pctChange > 8) return 'trending';
  if (pctChange > 3) return 'growing';
  if (pctChange > 0) return 'active';
  if (pctChange > -3) return 'stable';
  return 'declining';
}

/* ─── Ranking Position Change Indicator ─── */
export function getRankingChange(current: number, previous: number): { direction: 'up' | 'down' | 'same'; delta: number } {
  if (current < previous) return { direction: 'up', delta: previous - current };
  if (current > previous) return { direction: 'down', delta: current - previous };
  return { direction: 'same', delta: 0 };
}

/* ─── Format Helpers ─── */
export function formatScoreChange(change: number): string {
  if (change > 0) return `+${change.toLocaleString()} esta semana`;
  if (change < 0) return `${change.toLocaleString()} esta semana`;
  return 'Sin cambios esta semana';
}

export function formatScoreCompact(score: number): string {
  if (score >= 1000000) return `${(score / 1000000).toFixed(1)}M`;
  if (score >= 1000) return `${(score / 1000).toFixed(1)}K`;
  return score.toLocaleString();
}

export function formatGrowthPercent(percent: number): string {
  if (percent > 0) return `+${percent.toFixed(1)}%`;
  if (percent < 0) return `${percent.toFixed(1)}%`;
  return '0%';
}